import * as Sentry from '@sentry/react';
import { trackDesignSystemAction } from 'analytics';
import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import IconButton, { IconButtonState } from 'components/Widget/Button/IconButton';
import Pill from 'components/Widget/Pill';
import SubmitButton, { SubmitButtonState } from 'components/Widget/Button/SubmitButton';
import AttachmentStrip from 'components/Widget/AttachmentStrip';
import MultilineInput from 'components/Widget/Input/MultilineInput';
import ThumbnailStrip from 'components/Widget/ThumbnailStrip';
import VoiceInputButton from 'components/Widget/VoiceInputButton';
import { dsTabToPick, resolveDsPicksToDisplayNames } from 'designSystem/content/util';
import * as api from 'dragonmouse-shared/api/forge';
import { ChatMode, ChatStatus, DsChat, DsPick } from 'dragonmouse-shared/interfaces';
import { NEW_CHAT_TITLE } from 'dragonmouse-shared/constants';
import { designSystemEditingBlocked } from 'redux/db/util';
import { setDraftDsChat, setHistoricalDsVersionNumber } from 'redux/mode/actions';
import { displayDsReadOnlyModal } from 'redux/mode/util/modals';
import { startDsChatDraft } from 'redux/mode/util';
import { interceptIfNoCredits } from 'economy';
import { useDsChatQueue } from 'hooks/useDsChatQueue';
import { useFileUploadCallbacks } from 'hooks/useFileUploadCallbacks';
import useDsNavigation from 'hooks/useDsNavigation';
import useToast from 'hooks/useToast';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectActiveDesignSystem, selectFrame } from 'redux/db/reducers';
import { clearDsPicks, removeDsPick, setDsPicks, setPendingDsChatInput } from 'redux/mode/actions';
import { selectActiveDsTab, selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import store from 'redux/store';
import { useTheme } from 'styled-components';
import * as routes from 'routes';
import { cancelUpload, onUploadFileDialog, openInternalTab } from 'utils';
import { registerChatInputFocusTarget } from '../../hooks/chatInputFocus';
import { useChatInputHandlers } from '../../hooks/useChatInputHandlers';
import { useChatInputValidation } from '../../hooks/useChatInputValidation';
import * as Base from '../styles';
import BreakageNotification from './BreakageNotification';

interface DsChatInputProps {
	dsChat: DsChat;
	isLegacy?: boolean;
}

const DsChatInput = ({ dsChat, isLegacy }: DsChatInputProps) => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const { openPickTab } = useDsNavigation();
	const { message: toastMessage, isVisible: isToastVisible, showToast } = useToast();

	const {
		activeDesignSystem,
		pendingDsChatInput,
		isMobile,
		selectedDsPicks,
		designSystemContent,
		activeTab,
		historicalDsVersionNumber,
		draftDsChatId,
	} = useSelector((r: RootReducer) => {
		return {
			activeDesignSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
			pendingDsChatInput: r.mode.pendingDsChatInputs[dsChat.id],
			isMobile: selectIsMobile(r.mode),
			selectedDsPicks: r.mode.selectedDsPicks,
			designSystemContent: r.mode.designSystemContent,
			activeTab: selectActiveDsTab(r.mode),
			historicalDsVersionNumber: r.mode.historicalDsVersionNumber,
			draftDsChatId: r.mode.draftDsChat?.id ?? null,
		};
	}, shallowEqual);

	const isViewingHistorical = historicalDsVersionNumber !== null;
	const effectiveChatMode = isViewingHistorical
		? ChatMode.CHAT_ONLY
		: pendingDsChatInput?.chatMode || ChatMode.DESIGN_AND_CHAT;
	const [prompt, setPrompt] = useState<string>(pendingDsChatInput?.message || '');
	const [isFocused, setIsFocused] = useState<boolean>(false);
	const inputRef = useRef<HTMLTextAreaElement>(null);

	// lets the tab strip land the user typing, with the focus ring as the feedback
	useEffect(() => registerChatInputFocusTarget(inputRef), []);
	const lastSubmitRef = useRef<number>(0);
	// A draft's create request is the only submit with no server row to serialize
	// against, so re-entering it would post a second chat under the same id. Holds
	// submits until the request settles.
	const [isCreatingDraft, setIsCreatingDraft] = useState(false);

	useEffect(() => {
		if (!pendingDsChatInput) {
			dispatch(
				setPendingDsChatInput({
					dsChatId: dsChat.id,
					pendingDsChatInput: {
						message: '',
						chatMode: ChatMode.DESIGN_AND_CHAT,
					},
				})
			);
		}
	}, [dsChat.id, pendingDsChatInput, dispatch]);

	const stoppable = dsChat?.metadata?.status === ChatStatus.STOPPABLE;

	const queue = useDsChatQueue(dsChat?.id ?? null);

	// Validation logic for whether input can be submitted
	const { canSubmit } = useChatInputValidation({
		prompt,
		loadingAttachments: pendingDsChatInput?.loadingAttachments,
	});

	const resolvedDsPicks = useMemo(
		() => resolveDsPicksToDisplayNames(selectedDsPicks, designSystemContent ?? null),
		[selectedDsPicks, designSystemContent]
	);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onSubmit = async () => {
		// Guards a same-tick double-fire, before the cleared input has re-rendered and
		// turned canSubmit off.
		if (Date.now() - lastSubmitRef.current < 500) return;
		lastSubmitRef.current = Date.now();

		// Guard before the input is cleared, so a held submit keeps what was typed.
		if (isCreatingDraft) return;

		if (!activeDesignSystem || !dsChat) return;
		if (!canSubmit) return;

		const blocked = designSystemEditingBlocked();
		if (blocked && blocked !== 'historical') {
			displayDsReadOnlyModal(blocked);
			return;
		}

		// Check credits/wallet - interceptIfNoCredits shows appropriate modal per user type
		if (interceptIfNoCredits({ kind: 'chatEdit', designSystemId: dsChat.designSystemId })) {
			return;
		}

		const entry: api.AddUserEntryDsChatRequest['entry'] = {
			kind: 'user',
			message: prompt,
			attachments: pendingDsChatInput?.attachments || [],
			sourceFrameIds: pendingDsChatInput?.frameIds || [],
			...(selectedDsPicks.length > 0 && { selectedDsPicks: [...selectedDsPicks] }),
			...(activeTab && { viewingDsPick: dsTabToPick(activeTab) }),
			...(historicalDsVersionNumber !== null && {
				viewingDsVersionNumber: historicalDsVersionNumber,
			}),
		};

		// Take the message before doing any async work: the input is the record of what
		// hasn't been sent yet, so leaving it filled during a round-trip invites the user
		// to send twice.
		const picksBeforeSubmit = [...selectedDsPicks];
		setPrompt('');
		dispatch(clearDsPicks());
		dispatch(
			setPendingDsChatInput({
				dsChatId: dsChat.id,
				pendingDsChatInput: {
					...pendingDsChatInput,
					message: '',
					attachments: [],
					frameIds: [],
				},
			})
		);

		// A draft chat exists only on the client. Its first message creates the chat and
		// posts the message in one request, so there is never a window where the chat
		// exists without it. Later messages go through the action queue, which serializes
		// them against a chat that may already be answering.
		if (dsChat.id === draftDsChatId) {
			setIsCreatingDraft(true);
			try {
				await forge.createDsChatWithEntry(
					{
						id: dsChat.id,
						designSystemId: activeDesignSystem.id,
						title: NEW_CHAT_TITLE,
						entry,
						chatMode: effectiveChatMode,
					},
					true
				);
				// Clear the draft before releasing isCreatingDraft. Otherwise a second
				// Enter in that gap would call create-with-entry again under the same id.
				dispatch(setDraftDsChat(null));
			} catch (error) {
				// The optimistic chat is dropped on failure, so this stays a draft. Put back
				// everything the submit cleared rather than swallowing what the user typed.
				Sentry.captureException(error);
				setPrompt(entry.message);
				dispatch(setPendingDsChatInput({ dsChatId: dsChat.id, pendingDsChatInput }));
				if (picksBeforeSubmit.length > 0) dispatch(setDsPicks(picksBeforeSubmit));
				return;
			} finally {
				setIsCreatingDraft(false);
			}
			return;
		}

		// Submission goes through the chat action queue. Idle chat → fires
		// immediately; busy chat → waits in FIFO and fires when idle. User
		// can rapid-fire and the queue serializes.
		const trimmedPrompt = prompt.trim();
		const firstAttachmentName = entry.attachments?.[0]?.filename;
		const label = (trimmedPrompt || firstAttachmentName || 'Message').slice(0, 80);
		queue.enqueue({
			label,
			payload: { kind: 'addUserEntry', entry, chatMode: effectiveChatMode },
		});
	};

	const onStop = async () => {
		if (!activeDesignSystem || !dsChat) return;

		await forge.updateDsChat({
			update: {
				id: dsChat.id,
				metadata: {
					status: ChatStatus.PROCESSING,
					statusMessage: 'Stopping...',
				},
			},
		});
		const req: api.StopDsChatGenerationRequest = {
			dsChatId: dsChat.id,
		};
		await forge.stopDsChatGeneration.withToast()(req);
	};

	// Callback for updating prompt - handles both local state and Redux
	const onUpdatePrompt = useCallback(
		(prompt: string) => {
			setPrompt(prompt);
			dispatch(
				setPendingDsChatInput({
					dsChatId: dsChat.id,
					pendingDsChatInput: {
						...pendingDsChatInput,
						message: prompt,
					},
				})
			);
		},
		[setPrompt, dispatch, dsChat.id, pendingDsChatInput]
	);

	// Dictation appends the finished transcript to whatever was typed before
	// recording began.
	const promptAtVoiceStartRef = useRef('');
	const onVoiceStart = useCallback(() => {
		promptAtVoiceStartRef.current = prompt;
	}, [prompt]);
	const onVoiceText = useCallback(
		(text: string) => {
			const base = promptAtVoiceStartRef.current.trimEnd();
			onUpdatePrompt(base ? `${base} ${text}` : text);
		},
		[onUpdatePrompt]
	);

	// Parses frame IDs from pasted URLs. Frame URLs look like:
	// https://studio.moonchild.ai/project/:sceneId/:title?frameId=xxx&edit=false
	// returns true if the text contained frame URLs (suppresses default paste)
	const parseAndAttachFrameUrls = useCallback(
		(text: string): boolean => {
			const frameIds: string[] = [];
			for (const line of text.split('\n')) {
				try {
					const url = new URL(line.trim());
					if (url.origin !== window.location.origin) continue;
					const frameId = url.searchParams.get('frameId');
					if (frameId) frameIds.push(frameId);
				} catch {
					// not a URL
				}
			}
			if (frameIds.length > 0) {
				const existing = pendingDsChatInput?.frameIds || [];
				const merged = [...existing, ...frameIds.filter((id) => !existing.includes(id))];
				dispatch(
					setPendingDsChatInput({
						dsChatId: dsChat.id,
						pendingDsChatInput: { ...pendingDsChatInput, frameIds: merged },
					})
				);
				return true;
			}
			return false;
		},
		[dispatch, dsChat.id, pendingDsChatInput]
	);

	// File upload callbacks using Redux dispatch
	const fileCallbacks = useFileUploadCallbacks({
		type: 'ds',
		chatId: dsChat.id,
	});

	// Shared input handlers
	const { onKeyDown, onPaste, onContextMenu } = useChatInputHandlers({
		prompt,
		inputRef,
		canSubmit,
		onSubmit,
		onUpdatePrompt,
		fileCallbacks,
		chatEntries: dsChat.entries,
		pendingAttachments: pendingDsChatInput?.attachments,
		onBeforePaste: parseAndAttachFrameUrls,
	});

	const onRemoveThumbnail = (frameId: string) => {
		dispatch(
			setPendingDsChatInput({
				dsChatId: dsChat.id,
				pendingDsChatInput: {
					...pendingDsChatInput,
					frameIds: (pendingDsChatInput?.frameIds || []).filter((id) => id !== frameId),
				},
			})
		);
	};

	const onThumbnailClick = (frameId: string) => {
		const frame = selectFrame(store.getState().db, frameId);
		const sceneId = frame?.sceneId;
		if (sceneId) {
			const scene = store.getState().db.scenes.find((s) => s.id === sceneId);
			const url = routes.frame(sceneId, scene?.title || 'Untitled', frameId);
			openInternalTab(url);
		}
	};

	const onRemoveAttachment = (storagePath: string) => {
		dispatch(
			setPendingDsChatInput({
				dsChatId: dsChat.id,
				pendingDsChatInput: {
					...pendingDsChatInput,
					attachments: pendingDsChatInput.attachments?.filter((a) => a.storagePath !== storagePath),
				},
			})
		);
	};

	const onPickClick = (pick: DsPick) => {
		openPickTab(pick);
	};

	const onNewChat = () => {
		if (!activeDesignSystem) return;
		const blocked = designSystemEditingBlocked();
		if (blocked && blocked !== 'historical') {
			displayDsReadOnlyModal(blocked);
			return;
		}

		// The new chat is a client-only draft; it hits the server only once its first
		// message is sent.
		startDsChatDraft();
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	const renderDsPicks = () => {
		if (resolvedDsPicks.length === 0) return null;
		return (
			<Base.DsPicks>
				{resolvedDsPicks.map(({ pick, name }) => {
					const key =
						pick.kind === 'token'
							? `token-${pick.cssVariable}`
							: pick.kind === 'recipe'
							? `recipe-${pick.className}`
							: pick.kind === 'component'
							? `comp-${pick.tagName}`
							: pick.kind === 'guideline'
							? `guideline-${pick.guidelinePath}`
							: pick.kind === 'gallery'
							? `gallery-${pick.galleryPath}`
							: pick.kind === 'section'
							? `section-${pick.section}`
							: `asset-${pick.assetPath}`;
					return (
						<Pill
							key={key}
							label={name}
							tooltip="Click to open in design system"
							onClick={() => onPickClick(pick)}
							onRemove={() => dispatch(removeDsPick(pick))}
						/>
					);
				})}
				{resolvedDsPicks.length > 3 && (
					<Base.ClearAllButton onClick={() => dispatch(clearDsPicks())}>
						Clear all
					</Base.ClearAllButton>
				)}
			</Base.DsPicks>
		);
	};

	const renderAttachments = () => {
		const allAttachments = pendingDsChatInput?.attachments || [];
		const loading = pendingDsChatInput?.loadingAttachments;
		if (!allAttachments.length && !loading?.length) return null;
		return (
			<AttachmentStrip
				attachments={allAttachments}
				loadingAttachments={loading}
				onRemove={onRemoveAttachment}
				onCancel={cancelUpload}
			/>
		);
	};

	const renderAttachmentButton = () => {
		return (
			<IconButton
				icon={IconType.ADD}
				label={undefined}
				tooltip="Upload attachment"
				onClick={() => {
					onUploadFileDialog(fileCallbacks, true);
				}}
			/>
		);
	};

	const renderChatModeToggle = () => {
		const isChatOnly = effectiveChatMode === ChatMode.CHAT_ONLY;
		return (
			<IconButton
				state={isChatOnly ? IconButtonState.SELECTED : IconButtonState.REGULAR}
				icon={isChatOnly ? IconType.CHAT : IconType.STYLE}
				tooltip={isChatOnly ? 'Turn off Chat-only Mode' : 'Turn on Chat-only Mode'}
				colors={{
					selected: theme.green[200],
					hover: isChatOnly ? theme.green[400] : theme.gray[200],
					active: 'transparent',
					text: isChatOnly ? theme.green[800] : theme.gray[1000],
				}}
				onClick={() => {
					const nextChatMode =
						pendingDsChatInput?.chatMode === ChatMode.DESIGN_AND_CHAT
							? ChatMode.CHAT_ONLY
							: ChatMode.DESIGN_AND_CHAT;
					trackDesignSystemAction('toggle_chat_mode', { mode: nextChatMode });
					dispatch(
						setPendingDsChatInput({
							dsChatId: dsChat.id,
							pendingDsChatInput: {
								...pendingDsChatInput,
								chatMode: nextChatMode,
							},
						})
					);

					if (isMobile) {
						showToast(
							nextChatMode === ChatMode.CHAT_ONLY ? 'Chat-only mode' : 'Chat + design mode'
						);
					}
				}}
			/>
		);
	};

	const renderActions = () => {
		// Submit is available whenever there is something to send — submissions go
		// through the chat action queue, so they enqueue when the chat is busy. A
		// draft's create request is the exception: it has no queue behind it, so
		// submit is held until it settles. When the chat is stoppable we swap the
		// submit icon for stop (clicking stops the current run; the queue is
		// preserved). The processing-only spinner affordance is gone — the
		// QueueIndicator above the input shows in-flight/queued state.
		return (
			<Base.Actions isMobile={isMobile}>
				{!isMobile && renderAttachmentButton()}
				{!isViewingHistorical && renderChatModeToggle()}
				<Base.Spacer />
				<VoiceInputButton onRecordStart={onVoiceStart} onTranscript={onVoiceText} />
				<Base.SubmitButtonWrapper isMobile={isMobile}>
					<SubmitButton
						state={
							stoppable || (canSubmit && !isCreatingDraft)
								? SubmitButtonState.REGULAR
								: SubmitButtonState.DISABLED
						}
						colors={
							effectiveChatMode === ChatMode.CHAT_ONLY
								? {
										disabled: theme.green[200],
										hover: theme.green[600],
										active: theme.green[800],
								  }
								: undefined
						}
						type={stoppable ? IconType.STOP : IconType.LINE_ARROW_UP}
						tooltip={stoppable ? 'Stop' : 'Submit'}
						tooltipPlacement="bottom-start"
						onClick={() => (stoppable ? onStop() : onSubmit())}
						data-testid={stoppable ? 'ds-chat-stop-button' : 'ds-chat-submit-button'}
					/>
				</Base.SubmitButtonWrapper>
			</Base.Actions>
		);
	};

	return (
		<>
			{isMobile && toastMessage && (
				<Base.MobileToast isVisible={isToastVisible} role="status" aria-live="polite">
					{toastMessage}
				</Base.MobileToast>
			)}
			<Base.Base isMobile={isMobile} $hasConversation={dsChat.entries.length > 0}>
				{isLegacy && (
					<Base.LegacyOverlay isMobile={isMobile}>
						<Base.LegacyMessage isMobile={isMobile}>
							This chat uses a legacy format and can't accept new messages.
						</Base.LegacyMessage>
						<Base.LegacyNewChatButton isMobile={isMobile} onClick={onNewChat}>
							<Icon
								type={IconType.ADD}
								width={isMobile ? 16 : 14}
								height={isMobile ? 16 : 14}
								color={theme.gray[0]}
							/>
							Start New Chat
						</Base.LegacyNewChatButton>
					</Base.LegacyOverlay>
				)}
				<BreakageNotification dsChat={dsChat} />
				<Base.Prompt isFocused={isFocused}>
					{isViewingHistorical && (
						<Base.HistoricalRibbon>
							Viewing v{historicalDsVersionNumber} · chat-only mode
							<Base.RibbonLink onClick={() => dispatch(setHistoricalDsVersionNumber(null))}>
								Switch to latest
							</Base.RibbonLink>
						</Base.HistoricalRibbon>
					)}
					<ThumbnailStrip
						frameIds={pendingDsChatInput?.frameIds || []}
						onRemove={onRemoveThumbnail}
						onClick={onThumbnailClick}
					/>
					{renderDsPicks()}
					{renderAttachments()}
					<Base.MultilineInputWrapper isMobile={isMobile} onPaste={onPaste}>
						<MultilineInput
							data-ds-chat-input="true"
							data-testid="ds-chat-input"
							ref={inputRef}
							value={prompt}
							onChange={(e) => onUpdatePrompt(e.currentTarget.value)}
							onKeyDown={onKeyDown}
							placeholder={'Chat about your design system'}
							minLines={isMobile ? 1 : 4}
							renderBorder={false}
							variant={isMobile ? 'large' : 'medium'}
							onFocusChange={(isFocused) => {
								setIsFocused(isFocused);
							}}
							onContextMenu={onContextMenu}
						/>
					</Base.MultilineInputWrapper>
					{renderActions()}
				</Base.Prompt>
			</Base.Base>
		</>
	);
};

export default DsChatInput;
